/**
 * PATH: packages/email-hub/src/feature/components/InlineImageLightbox.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-INLINEIMAGELIGHTBOX-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/InlineImageLightbox.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/InlineImageLightbox.tsx:
 *     thin re-export shim that points back here.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Copy, Download, ExternalLink, Forward, Pen, Reply, X,
} from 'lucide-react'
import { useToast } from '../../_internal/components/Toast'
import {
  blobToFile,
  defaultFilenameFor,
  fetchImageBlob,
  writeImageToClipboard,
} from '@quotech/email-hub/feature/lib/imageClipboard'
import {
  useComposePendingStore,
  composeUrlFor,
  type ComposeIntent,
} from '../stores/composePendingStore'

export interface InlineImageLightboxProps {
  /** ``null`` means closed. We accept a single object instead of
   *  parallel `isOpen`/`src` props so the parent can't mistakenly
   *  render an "open" lightbox without a source. */
  image: { src: string; alt?: string } | null
  /** Currently-viewed email — needed for "Reply / Forward with this
   *  image". ``null`` if the operator hit the global markup
   *  shortcut and there's no anchor email. */
  emailId: string | null
  onClose: () => void
  /** Hand the image off to the markup drawer. Parent owns the
   *  drawer state because the markup drawer is also driven by the
   *  global Cmd+Shift+M shortcut. */
  onOpenInMarkup: (src: string) => void
}

export function InlineImageLightbox({
  image,
  emailId,
  onClose,
  onOpenInMarkup,
}: InlineImageLightboxProps) {
  const navigate = useNavigate()
  const { toast } = useToast()
  const setPending = useComposePendingStore((s) => s.setPending)

  useEffect(() => {
    if (!image) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [image, onClose])

  if (!image) return null

  const filename = filenameFromSrc(image.src, image.alt)

  async function handleCopy() {
    if (!image) return
    const blob = await fetchImageBlob(image.src)
    if (!blob) {
      toast('Could not load image bytes to copy', 'error')
      return
    }
    const ok = await writeImageToClipboard(blob)
    toast(
      ok ? 'Image copied to clipboard' : 'Browser blocked clipboard write',
      ok ? 'success' : 'error',
    )
  }

  async function handleDownload() {
    if (!image) return
    const blob = await fetchImageBlob(image.src)
    if (!blob) {
      toast('Could not download image', 'error')
      return
    }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  async function handlePushToCompose(intent: ComposeIntent) {
    if (!image) return
    const blob = await fetchImageBlob(image.src)
    if (!blob) {
      toast('Could not attach image', 'error')
      return
    }
    const file = blobToFile(blob, filename)
    setPending({ files: [file], intent })
    navigate(composeUrlFor(intent))
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={onClose}
    >
      <div
        className="relative max-w-[95vw] max-h-[90vh] flex flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt || 'inline email image'}
          className="max-w-full max-h-[80vh] object-contain rounded-md shadow-2xl bg-checker"
          draggable={false}
        />
        <div
          className="flex flex-wrap items-center justify-center gap-1.5 rounded-full bg-background/95 border border-border shadow-lg px-2 py-1.5"
        >
          <ToolbarButton icon={Download} label="Download" onClick={handleDownload} />
          <ToolbarButton icon={Copy} label="Copy" onClick={handleCopy} />
          <ToolbarButton
            icon={ExternalLink}
            label="Open in tab"
            onClick={() => window.open(image.src, '_blank', 'noopener,noreferrer')}
          />
          <span className="w-px h-5 bg-border mx-1" aria-hidden />
          {emailId && (
            <>
              <ToolbarButton
                icon={Reply}
                label="Reply with"
                onClick={() => handlePushToCompose({ kind: 'reply', emailId })}
              />
              <ToolbarButton
                icon={Forward}
                label="Forward with"
                onClick={() => handlePushToCompose({ kind: 'forward', emailId })}
              />
            </>
          )}
          <ToolbarButton
            icon={Pen}
            label="Mark up"
            onClick={() => {
              onOpenInMarkup(image.src)
              onClose()
            }}
          />
          <span className="w-px h-5 bg-border mx-1" aria-hidden />
          <ToolbarButton
            icon={X}
            label="Close"
            onClick={onClose}
            destructive
          />
        </div>
      </div>
    </div>
  )
}

function ToolbarButton({
  icon: Icon, label, onClick, destructive,
}: {
  icon: typeof X
  label: string
  onClick: () => void
  destructive?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ' +
        'hover:bg-accent transition-colors ' +
        (destructive ? 'text-destructive hover:bg-destructive/10' : 'text-foreground')
      }
      title={label}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

/** Try to coax a sensible filename out of the (usually opaque)
 *  inline-attachment signed URL. Falls back to a timestamp. */
function filenameFromSrc(src: string, alt?: string): string {
  if (alt && /\.(png|jpe?g|gif|webp)$/i.test(alt)) return alt
  try {
    const url = new URL(src, window.location.origin)
    const last = url.pathname.split('/').filter(Boolean).pop() || ''
    if (/\.(png|jpe?g|gif|webp)$/i.test(last)) return last
  } catch {
    /* fallthrough */
  }
  return defaultFilenameFor('image/png')
}
